// v3E.3 — Global Emergency SOS Floating Action Button.
// Mounted in Layout. Hidden on /admin/*, /business/*, /emergency/public/*, login/signup.
// Two-step: click → confirmation modal → trigger.
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Siren, X, AlertOctagon, Loader2, Phone, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";

const SosFAB = () => {
    const { user } = useAuth();
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [ambulanceCalled, setAmbulanceCalled] = useState(false);
    const [notes, setNotes] = useState("");

    const isHidden =
        !user ||
        pathname.startsWith("/admin") ||
        pathname.startsWith("/business") ||
        pathname.startsWith("/emergency/public") ||
        pathname === "/login" ||
        pathname === "/signup";

    const onTrigger = async () => {
        setSubmitting(true);
        let loc = {};
        try {
            await new Promise((resolve) => {
                if (!navigator.geolocation) return resolve();
                navigator.geolocation.getCurrentPosition(
                    (p) => { loc = { location_lat: p.coords.latitude, location_lng: p.coords.longitude }; resolve(); },
                    () => resolve(),
                    { timeout: 4000, enableHighAccuracy: false }
                );
            });
            const { data } = await api.post("/sos/trigger", { ...loc, ambulance_called: ambulanceCalled, notes });
            toast.success(`Contacts notified (simulated) — ${data.contacts_notified.length} alert${data.contacts_notified.length === 1 ? "" : "s"} sent`, { duration: 7000 });
            setOpen(false);
            setNotes("");
            setAmbulanceCalled(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not trigger SOS");
        } finally {
            setSubmitting(false);
        }
    };

    if (isHidden) return null;

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                data-testid="sos-fab-button"
                aria-label="Trigger emergency SOS"
                className="fixed bottom-6 left-6 z-[60] inline-flex h-14 items-center gap-2 rounded-full bg-gradient-to-br from-rose-600 via-red-600 to-red-500 px-5 text-white shadow-lg shadow-rose-700/40 ring-4 ring-rose-500/20 transition-transform hover:scale-105 active:scale-95 dark:shadow-rose-900/60"
            >
                <Siren className="h-5 w-5" />
                <span className="hidden text-sm font-bold uppercase tracking-wider sm:inline">SOS</span>
            </button>

            {open && (
                <div className="fixed inset-0 z-[80]" data-testid="sos-modal">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !submitting && setOpen(false)} />
                    <div className="absolute inset-0 grid place-items-center p-4">
                        <div className="w-full max-w-md overflow-hidden rounded-3xl border border-rose-200/60 bg-white shadow-2xl dark:border-rose-500/30 dark:bg-zinc-900">
                            <header className="flex items-center justify-between bg-gradient-to-r from-rose-600 to-red-600 px-5 py-4 text-white">
                                <div className="flex items-center gap-2">
                                    <AlertOctagon className="h-5 w-5" />
                                    <p className="text-sm font-semibold uppercase tracking-wider">Confirm SOS</p>
                                </div>
                                <button onClick={() => !submitting && setOpen(false)} className="rounded-full p-1 text-white/80 hover:bg-white/10" aria-label="Close" data-testid="sos-modal-close">
                                    <X className="h-4 w-4" />
                                </button>
                            </header>
                            <div className="space-y-4 p-6">
                                <div className="rounded-2xl border border-rose-100 bg-rose-50/60 p-4 text-sm text-rose-900 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
                                    <p className="font-semibold">This will alert your emergency contacts immediately.</p>
                                    <p className="mt-1 text-xs">
                                        Your location, last vitals and emergency profile will be shared with everyone you've added under <strong>Emergency Profile → Contacts</strong>.
                                    </p>
                                </div>
                                <label className="flex cursor-pointer items-start gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={ambulanceCalled}
                                        onChange={(e) => setAmbulanceCalled(e.target.checked)}
                                        className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-rose-600 focus:ring-rose-500"
                                        data-testid="sos-ambulance-check"
                                    />
                                    <span className="text-zinc-700 dark:text-zinc-200">
                                        <Phone className="mr-1 inline-block h-3.5 w-3.5" />
                                        I'm calling an ambulance too (let contacts know)
                                    </span>
                                </label>
                                <textarea
                                    rows={2}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Optional note (e.g. 'collapsed at home', 'severe chest pain')"
                                    maxLength={400}
                                    className="w-full rounded-2xl border border-zinc-200 bg-white px-3 py-2 text-sm outline-none ring-rose-200 focus:ring-2 dark:border-white/10 dark:bg-white/[0.04] dark:text-zinc-50"
                                    data-testid="sos-notes-input"
                                />
                                <p className="rounded-xl bg-amber-50 px-3 py-2 text-[11px] text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
                                    <ShieldAlert className="mr-1 inline-block h-3 w-3" />
                                    Demo mode: contact alerts are <strong>simulated</strong>. Real SMS/WhatsApp delivery launches in the next phase.
                                </p>
                                <div className="flex justify-end gap-2 pt-2">
                                    <button onClick={() => setOpen(false)} disabled={submitting} className="rounded-full border border-zinc-200 px-4 py-2 text-sm hover:bg-zinc-50 dark:border-white/10 dark:hover:bg-white/5" data-testid="sos-cancel-btn">Cancel</button>
                                    <button onClick={onTrigger} disabled={submitting} className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-rose-600 to-red-600 px-5 py-2 text-sm font-semibold text-white shadow-md disabled:opacity-60" data-testid="sos-confirm-btn">
                                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Siren className="h-4 w-4" />}
                                        {submitting ? "Sending..." : "Trigger SOS now"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SosFAB;
